import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { clsx, getAverageColor } from '../../utils';
import { Colors } from '../../constants';

enum BackgroundTypeEnum {
	Custom = 'custom',
	Default = 'default',
}

export interface EventBackgroundProps {
	sx?: SxProps<Theme>;
	background?: string;
}

const animateBackground = keyframes`
	0% {
		top: 7%;
		left: 5%;
	}
	17% {
		top: 38%;
		left: 60%;
	}
	31% {
		top: 4%;
		left: 98%;
	}
	53% {
		top: 96%;
		left: 79%;
	}
	65% {
		top: 61%;
		left: 48%;
	}
	80% {
		top: 96%;
		left: 3%;
	}
	100% {
		top: 7%;
		left: 5%;
	}
`;

export const EventBackground: FC<EventBackgroundProps> = ({ background, sx }) => {
	const [backgroundType, setBackgroundType] = useState<BackgroundTypeEnum | null>(null);
	const backgroundRef = useRef<HTMLDivElement | null>(null);

	const getEventBackgroundType = useCallback(async (): Promise<void> => {
		if (!background) {
			setBackgroundType(BackgroundTypeEnum.Default);

			return;
		}

		try {
			const color = await getAverageColor(background);

			setBackgroundType(color ? BackgroundTypeEnum.Custom : BackgroundTypeEnum.Default);
		} catch (e) {
			setBackgroundType(BackgroundTypeEnum.Custom);
		}
	}, [background]);

	useEffect(() => {
		getEventBackgroundType();
	}, [getEventBackgroundType]);

	if (backgroundType === null) {
		return null;
	}

	return (
		<Box
			sx={[
				{
					position: 'absolute',
					top: 0,
					left: 0,
					zIndex: 0,
					width: '100%',
					height: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					overflow: 'hidden',
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
			ref={backgroundRef}
		>
			<Box
				sx={clsx(
					{
						position: 'absolute',
						width: '725px',
						height: '1200px',
						top: 0,
						left: 0,
						transform: 'translate(-40%, -40%) rotate(45deg)',
						borderRadius: { xs: 0, sm: '50%' },
						willChange: 'left, top',
						animation: { sm: `${animateBackground} infinite linear` },
						animationDuration: {
							sm: '20s',
							md: '25s',
							lg: '30s',
							xl: '35s',
						}
					},
					backgroundType === BackgroundTypeEnum.Default ? {
						backgroundColor: Colors['333333'],
					} : background ? {
						backgroundImage: `url(${background})`,
						backgroundSize: 'cover',
						filter: 'saturate(3)',
					} : undefined,
				)}
			/>
			<Box
				sx={clsx(
					{
						position: 'absolute',
						width: '100%',
						height: '100%',
					},
					backgroundType === BackgroundTypeEnum.Default ? {
						backdropFilter: 'blur(200px)',
					} : background ? {
						backdropFilter: 'blur(140px)',
					} : undefined,
				)}
			/>
		</Box>
	);
};
